import { useEffect, useState } from "react";
import { BellRing, X, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { isPushSupported, pushPermission, hasActiveSubscription, enablePush } from "@/lib/push";
import IOSInstallHelp from "@/components/IOSInstallHelp";
import AndroidInstallHelp from "@/components/AndroidInstallHelp";

const DISMISS_KEY = "bkr_push_banner_dismissed_at";
const RE_ASK_MS = 7 * 24 * 60 * 60 * 1000; // 7 dagen na dismiss

export default function BKRPushBanner() {
  const { user } = useAuth();
  const [visible, setVisible] = useState(false);
  const [state, setState] = useState({ supported: false, permission: "default", subscribed: false });
  const [busy, setBusy] = useState(false);

  const eligibleRole = ["superadmin", "platform_admin", "locatiemanager"].includes(user?.role);

  useEffect(() => {
    if (!eligibleRole) return;
    const dismissed = Number(localStorage.getItem(DISMISS_KEY) || 0);
    if (dismissed && Date.now() - dismissed < RE_ASK_MS) return;
    (async () => {
      const supported = isPushSupported();
      const permission = pushPermission();
      const subscribed = supported && permission === "granted" ? await hasActiveSubscription() : false;
      setState({ supported, permission, subscribed });
      // Toon banner als: rol eligible + browser ondersteunt push + nog niet subscribed
      // (ook bij "denied" tonen we een uitleg-versie i.p.v. verbergen)
      if (supported && !subscribed) setVisible(true);
    })();
  }, [eligibleRole]);

  const enable = async () => {
    setBusy(true);
    try {
      const r = await enablePush();
      if (r.ok) {
        toast.success("Meldingen geactiveerd — BKR-rood komt nu direct binnen");
        setVisible(false);
      } else if (r.reason === "denied") {
        toast.error("Meldingen zijn geblokkeerd — activeer ze via je browser-instellingen (slot-icoon in de adresbalk)");
      } else if (r.reason === "unsupported") {
        toast.error("Deze browser ondersteunt geen push-meldingen");
      } else {
        toast.error(`Activering mislukt: ${r.reason}`);
      }
    } finally { setBusy(false); }
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setVisible(false);
  };

  if (!eligibleRole || !visible) return null;

  const isDenied = state.permission === "denied";
  const bgTone = isDenied ? "border-red-300 bg-red-50" : "border-amber-300 bg-amber-50";
  const iconBg = isDenied ? "bg-red-100" : "bg-amber-100";
  const iconColor = isDenied ? "text-red-700" : "text-amber-700";
  const titleColor = isDenied ? "text-red-900" : "text-amber-900";
  const bodyColor = isDenied ? "text-red-800" : "text-amber-800";

  return (
    <div
      className={`mb-4 rounded-lg border p-3 sm:p-4 flex items-start gap-3 ${bgTone}`}
      data-testid="bkr-push-banner"
    >
      <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${iconBg}`}>
        <BellRing className={`h-4 w-4 ${iconColor}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-medium ${titleColor}`}>
          {isDenied ? "Browser-meldingen staan uit" : "Activeer browser-meldingen voor BKR-alarmen"}
        </div>
        <div className={`text-xs mt-0.5 ${bodyColor}`}>
          {isDenied
            ? "Je hebt meldingen eerder geblokkeerd. Klik op het slot-icoon links in de adresbalk → Meldingen → Toestaan. Zonder meldingen mis je BKR-rood-alarmen op dit apparaat."
            : "Zodra een stamgroep in rode BKR-status komt, ontvang je direct een melding op dit apparaat. Handig als je onderweg bent tussen locaties."
          }
        </div>
        <div className="flex flex-wrap gap-2 mt-2.5">
          {!isDenied && (
            <Button
              size="sm"
              onClick={enable}
              disabled={busy}
              style={{ background: "var(--sage)" }}
              className="text-white h-8 px-3 text-xs"
              data-testid="bkr-push-enable-btn"
            >
              <CheckCircle2 className="h-3.5 w-3.5 mr-1" />
              {busy ? "Activeren…" : "Meldingen aanzetten"}
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            onClick={dismiss}
            className={`h-8 px-3 text-xs ${isDenied ? "text-red-700 hover:bg-red-100" : "text-amber-700 hover:bg-amber-100"}`}
            data-testid="bkr-push-dismiss-btn"
          >
            {isDenied ? "Verbergen (7 dagen)" : "Later"}
          </Button>
          <div className="ml-auto flex items-center gap-3">
            <IOSInstallHelp compact />
            <AndroidInstallHelp compact />
          </div>
        </div>
      </div>
      <button
        onClick={dismiss}
        className={`p-1 -m-1 shrink-0 ${isDenied ? "text-red-700 hover:text-red-900" : "text-amber-700 hover:text-amber-900"}`}
        aria-label="Sluiten"
        data-testid="bkr-push-close"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
